import Typography from '@mui/material/Typography'
import { Box, Container } from '@mui/material'
import PlayCircleOutlinedIcon from '@mui/icons-material/PlayCircleOutlined'

import { useAppSelector } from 'src/store/hooks'
import {
  selectActionableCourseProgress,
  selectCourseProgressHasLoadedOnce,
  selectCourses,
  selectCoursesStatus,
} from 'src/v3/self-paced/self-paced.slice'
import { ReactElement } from 'react'
import { useNavigate } from 'react-router-dom'
import { ActionItemCard } from './components/action-item-card'
import { ContinueWatchingPageSkeleton } from './components/skeletons'
import { EmptyState } from './components/empty-state'

export default (): ReactElement => {
  let navigate = useNavigate()
  let actionableProgress = useAppSelector(selectActionableCourseProgress)
  let courses = useAppSelector(selectCourses)
  let coursesStatus = useAppSelector(selectCoursesStatus)
  let courseProgressHasLoadedOnce = useAppSelector(
    selectCourseProgressHasLoadedOnce,
  )
  // Progress can be ready before the separate courses fetch resolves — each
  // ActionItemCard looks up its course and renders nothing without one, so
  // without this the grid would render blank instead of staying on skeleton.
  let isLoaded = courseProgressHasLoadedOnce && coursesStatus === 'fulfilled'

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1">
          Continue Watching
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Pick up where you left off
        </Typography>
      </Box>

      {!isLoaded ? (
        <ContinueWatchingPageSkeleton />
      ) : actionableProgress.length === 0 ? (
        <EmptyState
          icon={<PlayCircleOutlinedIcon />}
          title="Nothing needs your attention"
          description="Once you begin making progress in a course, or a quiz or survey is left to finish, it will appear here."
          actionText="Start Learning"
          onAction={() => navigate('/self-paced')}
        />
      ) : (
        <Box
          sx={{
            display: 'grid',
            gap: 3,
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
          }}
        >
          {actionableProgress.map((progress) => (
            <ActionItemCard
              key={progress.courseID}
              progress={progress}
              course={courses.find((c) => c.id === progress.courseID)}
              onNavigate={navigate}
            />
          ))}
        </Box>
      )}
    </Container>
  )
}
